import {
  Button,
  Label,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Progress,
  RadioGroup,
  RadioGroupItem,
} from "@repo/ui";
import { useFormStore } from "../../stores/formStore";
import { useFormPageNavigation } from "../../hooks/useFormPageNavigation";
import { FormTransitionOverlay } from "../../components/FormTransitionOverlay";
import { cn } from "@repo/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "convex/react";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { api } from "@convex/_generated/api";
import { TemplateFormEnum, TypeEvalEnum } from "@convex/schema";
import {
  isBackendError,
  type AutodiagReponse,
  type AutodiagReponses,
  type FormTemplateProps,
} from "../../lib/types";
import {
  AUTODIAG_QUESTIONS_PER_PAGE,
  autodiagOptions,
  autodiagQuestions,
} from "./autodiag-data";

// Construit le schéma Zod à partir des questions
const schema = z.object(
  Object.fromEntries(
    autodiagQuestions
      .flatMap((q) => [q, ...(q.sous_questions || [])])
      .map((q) => [
        q.name,
        // Message porté par `.min(1)` et pas seulement par `required_error` :
        // les champs sont initialisés à "" (cf. `initialDefaults`), jamais
        // `undefined`. C'est donc TOUJOURS `.min(1)` qui échoue, et sans message
        // explicite le candidat lisait « String must contain at least 1
        // character(s) » — du Zod brut, en anglais.
        z
          .string({ required_error: "Réponse requise." })
          .min(1, "Réponse requise."),
      ]),
  ),
);

type FormData = z.infer<typeof schema>;

export function CandidateAutodiagTemplate({
  token,
  parcoursId,
  editable,
  evaluation,
  onComplete,
}: FormTemplateProps) {
  const { isSubmitting, setIsSubmitting } = useFormStore();

  const submitEvaluation = useMutation(api.evaluations.soumettreEvaluation);
  const updateResponses = useMutation(api.evaluations.mettreAJourReponses);

  const totalPages = Math.ceil(
    autodiagQuestions.length / AUTODIAG_QUESTIONS_PER_PAGE,
  );

  // Initialise tous les champs à la chaîne vide, sinon React avertit du passage
  // d'un champ non contrôlé à contrôlé
  const allQuestions = autodiagQuestions.flatMap((q) => [
    q,
    ...(q.sous_questions || []),
  ]);
  const initialDefaults = Object.fromEntries(
    allQuestions.map((q) => [q.name, ""]),
  ) as FormData;

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: (
      Object.values(evaluation.donnees.reponses) as AutodiagReponse[]
    ).reduce(
      (acc: FormData, q) => ({
        ...acc,
        [q.name]: q.reponse,
        ...q.sous_questions?.reduce(
          (subAcc: FormData, sq) => ({
            ...subAcc,
            [sq.name]: sq.reponse,
          }),
          {},
        ),
      }),
      initialDefaults,
    ) as FormData,
  });


  // Construit l'objet donnees destiné au backend
  const buildDonnees = (data: FormData, pageActuelle?: number) => ({
    type: TypeEvalEnum.FORM as const,
    template: TemplateFormEnum.AUTODIAG,
    ...(pageActuelle !== undefined && { pageActuelle }),
    reponses: autodiagQuestions.reduce<AutodiagReponses>(
      (acc, q) => ({
        ...acc,
        [q.name]: {
          titre: q.titre,
          name: q.name,
          label: q.label,
          reponse: data[q.name as keyof FormData] as string,
          sous_questions: q.sous_questions?.map((sq) => ({
            name: sq.name,
            label: sq.label,
            reponse: data[sq.name as keyof FormData] as string,
          })),
        },
      }),
      {},
    ),
  });

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);

    try {
      const result = await submitEvaluation({
        evaluationId: evaluation._id,
        parcoursId,
        token,
        donnees: buildDonnees(data),
      });

      if (isBackendError(result)) {
        console.error("Erreur:", result.erreur);
      } else {
        onComplete?.();
      }
    } catch (error) {
      console.error("Erreur lors de la soumission:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const saveResponses = async (data: FormData, pageActuelle: number) => {
    try {
      await updateResponses({
        evaluationId: evaluation._id,
        parcoursId,
        token,
        donnees: buildDonnees(data, pageActuelle),
      });
    } catch (error) {
      console.error("Erreur lors de la sauvegarde:", error);
    }
  };

  const { currentPage, isNavigating, showTransitionOverlay, goToPage } =
    useFormPageNavigation<FormData>({
      pageCount: totalPages,
      pageActuelle: evaluation.donnees.pageActuelle,
      save: saveResponses,
    });

  const startIndex = currentPage * AUTODIAG_QUESTIONS_PER_PAGE;
  const currentQuestion = autodiagQuestions[startIndex];

  if (!currentQuestion) {
    return <div>Chargement...</div>;
  }

  const validateCurrentPage = async () => {
    const currentFields = [
      currentQuestion.name,
      ...(currentQuestion.sous_questions?.map((q) => q.name) || []),
    ];
    return await form.trigger(currentFields);
  };

  const handleNext = async () => {
    const isValid = await validateCurrentPage();
    if (isValid) {
      await goToPage(currentPage + 1, form.getValues());
    }
  };

  const progressPercentage = ((currentPage + 1) / totalPages) * 100;

  const isFirstPage = currentPage === 0;
  const isLastPage = currentPage === totalPages - 1;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 mx-auto container py-8">
        <div className="space-y-2">
          <h1 className="text-2xl font-bold">Autodiagnostic</h1>
          <p className="text-muted-foreground">
            Test d'auto diagnostic - 60 questions
          </p>
        </div>

        <div className="space-y-2">
          <div className="flex justify-between text-sm text-muted-foreground">
            <span>
              Page {currentPage + 1} sur {totalPages}
            </span>
          </div>
          <Progress value={progressPercentage} className="h-2" />
        </div>

        <div
          className={cn(
            "space-y-4 transition-opacity",
            isNavigating && "pointer-events-none opacity-50",
          )}
          aria-busy={isNavigating}
        >
          {currentQuestion.sous_questions &&
            currentQuestion.sous_questions.map((q) => (
              <FormField
                key={q.name}
                control={form.control}
                name={q.name}
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between p-4">
                    <Label
                      htmlFor={q.name}
                      className="flex lg:w-[50%] cursor-pointer pr-4"
                    >
                      <div className="flex flex-col">
                        {q.label}
                        <FormMessage />
                      </div>
                    </Label>
                    <FormControl>
                      <RadioGroup
                        value={field.value}
                        onValueChange={field.onChange}
                        disabled={!editable}
                        className="w-fit flex flex-row flex-1 justify-evenly"
                      >
                        {autodiagOptions.map((option) => (
                          <div
                            className="flex flex-col items-center gap-3"
                            key={option.value}
                          >
                            <RadioGroupItem
                              value={option.value.toString()}
                              id={`option-${q.name}-${option.value}`}
                            />
                            <Label htmlFor={`option-${q.name}-${option.value}`}>
                              {option.label}
                            </Label>
                          </div>
                        ))}
                      </RadioGroup>
                    </FormControl>
                  </FormItem>
                )}
              />
            ))}
          <FormField
            key={currentQuestion.name}
            control={form.control}
            name={currentQuestion.name}
            render={({ field }) => (
              <FormItem className="flex items-center justify-between p-4 rounded-surface bg-primary/5">
                <Label
                  htmlFor={currentQuestion.name}
                  className="flex lg:w-[50%] cursor-pointer pr-4"
                >
                  <div className="flex flex-col">
                    <span>{currentQuestion.label}</span>
                    <FormMessage />
                  </div>
                </Label>
                <FormControl>
                  <RadioGroup
                    value={field.value}
                    onValueChange={field.onChange}
                    disabled={!editable}
                    className="w-fit flex flex-row flex-1 justify-evenly"
                  >
                    {autodiagOptions.map((option) => (
                      <div
                        className="flex flex-col items-center gap-3"
                        key={option.value}
                      >
                        <RadioGroupItem
                          value={option.value.toString()}
                          id={`option-main-${option.value}`}
                        />
                        <Label htmlFor={`option-main-${option.value}`}>
                          {option.label}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <div className="flex justify-between gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => goToPage(currentPage - 1, form.getValues())}
            disabled={isFirstPage || isNavigating}
            className="flex-1"
          >
            <ChevronLeft className="mr-2 h-4 w-4" />
            Precedent
          </Button>

          {isLastPage ? (
            <Button
              type="submit"
              className="flex-1"
              disabled={isSubmitting || isNavigating || !editable}
            >
              {isSubmitting ? "Envoi en cours..." : "Soumettre"}
            </Button>
          ) : (
            <Button
              type="button"
              onClick={handleNext}
              disabled={isNavigating}
              className="flex-1"
            >
              {isNavigating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                <>
                  Suivant
                  <ChevronRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          )}
        </div>

        <FormTransitionOverlay open={showTransitionOverlay} />
      </form>
    </Form>
  );
}
